import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppInsights } from 'applicationinsights-js';
import moment from 'moment';
import { CookieService } from 'ngx-cookie';
import { v4 as uuidv4 } from 'uuid';
import { RootState } from '../store';
import { Customer, UserContext } from '../store/session/user-context/user-context.interface';

@Injectable({ providedIn: 'root' })
export class AzureMonitoringService {
  private logId = 0;
  private sessionId;
  private isInitialized = false;
  private activeCustomer: Customer;
  private selectedCustomer: Customer;
  private userContext: UserContext;

  get customerId(): string {
    return this._customerId;
  }

  set customerId(value: string) {
    if (this._customerId && this._customerId !== value) {
      this.logEvent(`customerId changed from '${this._customerId}' to '${value}'`);
    }
    this._customerId = value;
  }

  loaded: boolean = false;
  verboseLogging: boolean = false;
  // tslint:disable-next-line:variable-name
  private _customerId: string;
  private loginHint: string;

  constructor(cookieService: CookieService, private store: Store<RootState>) {
    this.loginHint = cookieService.get('login_hint');
    this.sessionId = `${this.loginHint}-${moment().format('YYYY-MM-DD HH:mm:ss SSS')}-${uuidv4()}`;
    store
      .select(state => { return [state.session.userContext, state.session._activeCustomer?.data, state.session.selectedCustomer?.data]; })
      .subscribe(([userContext, activeCustomer, selectedCustomer]: [UserContext, Customer, Customer]) => {
        this.userContext = userContext;
        this.activeCustomer = activeCustomer;
        this.selectedCustomer = selectedCustomer;
      });
  }

  init(instrumentationKey: string, verboseLogging: boolean) {
    if (!this.isInitialized) {
      this.isInitialized = true;
      AppInsights.downloadAndSetup({
        instrumentationKey,
      });
      this.loaded = true;
      this.verboseLogging = verboseLogging;
    }
  }

  logPageView(name?: string, url?: string, properties?: any, measurements?: any, duration?: number) {
    this.isInitialized && this.verboseLogging && AppInsights.trackPageView(name, url, this.enrichLogProperties(properties), measurements, duration);
  }

  logEvent(name: string, properties?: any, measurements?: any) {
    this.isInitialized && this.verboseLogging && AppInsights.trackEvent(name, this.enrichLogProperties(properties), measurements);
  }

  logException(exception: Error, properties?: any, measurements?: any) {
    this.isInitialized && AppInsights.trackException(exception, 'not_used', this.enrichLogProperties(properties), measurements);
  }

  flush() {
    AppInsights.flush();
  }

  private enrichLogProperties(properties: any) {
    return {
      logId: `00000${++this.logId}`.substr(-5),
      sessionId: this.sessionId,
      customer: this._customerId,
      loginHint: this.loginHint,
      email: this.userContext?.email,
      firstName: this.userContext?.firstName,
      lastName: this.userContext?.lastName,
      isEpsUser: this.userContext?.isEpsUser,
      isSuperUser: this.userContext?.isSuperUser,
      activeCustomer: this.activeCustomer ? `${this.activeCustomer.id} - ${this.activeCustomer.name}` : null,
      selectedCustomer: this.selectedCustomer ? `${this.selectedCustomer.id} - ${this.selectedCustomer.name}` : null,
      ...(properties || {}),
    };
  }
}
